import React from 'react';
import { I18nProvider } from '../i18n';
import { LanguageSelector } from './LanguageSelector';
import { SlideOne } from './SlideOne';
import { SlideTwo } from './SlideTwo';
import { SlideThree } from './SlideThree';
import { SlideFour } from './SlideFour';
import { SlideFive } from './SlideFive';
import { CountdownTimer } from './CountdownTimer';
import { ProgressIndicator } from './ProgressIndicator';
import { Footer } from './Footer';
interface ReviewDocumentProps {
  onClose: () => void;
}
function PhoneFrame({
  children,
  showTimer = false,
  showProgress = false,
  currentSlide = 1
}: {
  children: React.ReactNode;
  showTimer?: boolean;
  showProgress?: boolean;
  currentSlide?: number;
}) {
  return <div className="w-[280px] h-[600px] bg-solventum-bg rounded-[2.5rem] border-[8px] border-gray-800 overflow-hidden relative flex-shrink-0 shadow-xl flex flex-col" data-id="element-63">
      {/* Notch */}
      <div className="absolute top-0 inset-x-0 h-6 bg-solventum-bg z-30 flex justify-center" data-id="element-64">
        <div className="w-20 h-4 bg-gray-800 rounded-b-xl" data-id="element-65" />
      </div>
      {/* Home indicator */}
      <div className="absolute bottom-1.5 inset-x-0 z-30 flex justify-center" data-id="element-66">
        <div className="w-24 h-1 bg-white/20 rounded-full" data-id="element-67" />
      </div>

      {/* App content wrapper */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden mt-4 pb-4 flex flex-col" data-id="element-68">
        <div className="min-h-full w-full bg-solventum-bg flex flex-col items-center justify-between px-5 py-3 overflow-hidden selection:bg-solventum-primary selection:text-solventum-bg" data-id="element-69">
          {/* Header area */}
          <div className="w-full flex items-center justify-between pt-1 pb-2 min-h-[44px]" data-id="element-70">
            {currentSlide >= 1 && <button className="flex items-center gap-1.5 text-white/50 text-xs font-body" data-id="element-71">
                <span className="text-base" data-id="element-72">🇬🇧</span>
              </button>}
            {showTimer && <CountdownTimer elapsed={14} stopped={currentSlide >= 4} data-id="element-73" />}
          </div>

          {/* Main content */}
          <main className="flex-1 w-full flex flex-col justify-center relative" data-id="element-74">
            {children}
          </main>

          {/* Footer area */}
          <div className="w-full flex flex-col items-center mt-3" data-id="element-75">
            {showProgress && <ProgressIndicator total={3} current={currentSlide} data-id="element-76" />}
            <Footer data-id="element-77" />
          </div>
        </div>
      </div>
    </div>;
}
function FeedbackArea({
  title,
  description
}: {
  title: string;
  description: string;
}) {
  return <div className="flex-1 flex flex-col min-w-0" data-id="element-78">
      <div className="mb-5" data-id="element-79">
        <h3 className="text-xl font-extrabold text-gray-900 mb-2" data-id="element-80">{title}</h3>
        <p className="text-gray-600 text-sm leading-relaxed" data-id="element-81">{description}</p>
      </div>

      <div className="bg-gray-50 rounded-xl border border-gray-200 p-5 flex-1 flex flex-col gap-4" data-id="element-82">
        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-83">
          Customer Feedback
        </p>

        <div className="space-y-4 flex-1" data-id="element-84">
          <div data-id="element-85">
            <label className="block text-sm font-bold text-gray-700 mb-1.5" data-id="element-86">
              🎨 Design & Layout
            </label>
            <div className="w-full h-16 bg-white border border-gray-200 rounded-lg border-dashed" data-id="element-87"></div>
          </div>
          <div data-id="element-88">
            <label className="block text-sm font-bold text-gray-700 mb-1.5" data-id="element-89">
              ✍️ Wording & Copy
            </label>
            <div className="w-full h-16 bg-white border border-gray-200 rounded-lg border-dashed" data-id="element-90"></div>
          </div>
          <div data-id="element-91">
            <label className="block text-sm font-bold text-gray-700 mb-1.5" data-id="element-92">
              🖼️ Images & Visuals
            </label>
            <div className="w-full h-16 bg-white border border-gray-200 rounded-lg border-dashed" data-id="element-93"></div>
          </div>
          <div data-id="element-94">
            <label className="block text-sm font-bold text-gray-700 mb-1.5" data-id="element-95">
              ⚙️ Interactions & Flow
            </label>
            <div className="w-full h-16 bg-white border border-gray-200 rounded-lg border-dashed" data-id="element-96"></div>
          </div>
        </div>
      </div>
    </div>;
}
export function ReviewDocument({
  onClose
}: ReviewDocumentProps) {
  const noop = () => {};
  return <div className="bg-white min-h-screen" data-id="element-97">
      <style data-id="element-98">{`
        @media print {
          .no-print { display: none !important; }
          .review-row { page-break-inside: avoid; }
          @page { margin: 1.2cm; size: A4; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      {/* Sticky nav */}
      <div className="no-print sticky top-0 z-50 bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center shadow-sm" data-id="element-99">
        <div className="flex items-center gap-2" data-id="element-100">
          <div className="w-7 h-7 rounded-full bg-[#01332b] flex items-center justify-center" data-id="element-101">
            <span className="text-[#05dd4d] font-bold text-xs" data-id="element-102">R</span>
          </div>
          <span className="font-bold text-gray-800 text-sm" data-id="element-103">
            Review Document
          </span>
        </div>
        <div className="flex gap-3" data-id="element-104">
          <button onClick={() => window.print()} className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold px-3 py-1.5 rounded-lg transition-colors" data-id="element-105">
            🖨️ Print / Save PDF
          </button>
          <button onClick={onClose} className="text-xs bg-[#01332b] hover:bg-[#024a3e] text-white font-bold px-3 py-1.5 rounded-lg transition-colors" data-id="element-106">
            ← Back to Prototype
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10" data-id="element-107">
        {/* Cover */}
        <div className="mb-12 pb-8 border-b-4 border-[#05dd4d]" data-id="element-108">
          <p className="text-[#01332b] text-xs font-bold uppercase tracking-[0.2em] mb-3" data-id="element-109">
            Solventum Dental
          </p>
          <h1 className="text-3xl font-extrabold text-gray-900 mb-2" data-id="element-110">
            SNAK Review Document
          </h1>
          <h2 className="text-lg text-gray-500 mb-6" data-id="element-111">
            Interactive Sales Narrative Activation Kit
          </h2>
          <div className="bg-gray-50 rounded-xl p-5 border border-gray-200" data-id="element-112">
            <p className="text-gray-700 leading-relaxed text-sm" data-id="element-113">
              This document contains the actual rendered screens of the SNAK
              application. Please review the design, wording, images, and
              overall flow. Use the structured feedback areas next to each
              screen to provide your comments.
            </p>
          </div>
        </div>

        <I18nProvider lang="en" data-id="element-114">
          <div className="space-y-16" data-id="element-115">
            {/* Screen 0: Language */}
            <div className="review-row flex gap-10 pb-12 border-b border-gray-200" data-id="element-116">
              <PhoneFrame currentSlide={0} data-id="element-117">
                <LanguageSelector onSelect={noop} data-id="element-118" />
              </PhoneFrame>
              <FeedbackArea title="Screen 1: Language Selection & Hook" description="The opening screen where users select their preferred language. The pulsing '18' visual sets the hook before the timer even starts." data-id="element-119" />
            </div>

            {/* Screen 1: Poll */}
            <div className="review-row flex gap-10 pb-12 border-b border-gray-200" data-id="element-120">
              <PhoneFrame showTimer showProgress currentSlide={1} data-id="element-121">
                <SlideOne onNext={noop} onCTA={noop} data-id="element-122" />
              </PhoneFrame>
              <FeedbackArea title="Screen 2: The Poll" description="The first interactive element. Users self-identify their motivation. The timer runs in the top right." data-id="element-123" />
            </div>

            {/* Screen 2: Results */}
            <div className="review-row flex gap-10 pb-12 border-b border-gray-200" data-id="element-124">
              <PhoneFrame showTimer showProgress currentSlide={2} data-id="element-125">
                <SlideTwo onNext={noop} onBack={noop} onCTA={noop} selectedOption={0} data-id="element-126" />
              </PhoneFrame>
              <FeedbackArea title="Screen 3: Peer Results" description="Visualizes the poll result for their chosen option, accompanied by a personalized hero image and copy." data-id="element-127" />
            </div>

            {/* Screen 3: Proof */}
            <div className="review-row flex gap-10 pb-12 border-b border-gray-200" data-id="element-128">
              <PhoneFrame showTimer showProgress currentSlide={3} data-id="element-129">
                <SlideThree selectedOption={0} onBack={noop} onCTA={noop} data-id="element-130" />
              </PhoneFrame>
              <FeedbackArea title="Screen 4: The Proof" description="Features the 20→2 animation and Dr. Selliseth's testimonial. This is the core proof point of the experience." data-id="element-131" />
            </div>

            {/* Screen 4: CTA Options */}
            <div className="review-row flex gap-10 pb-12 border-b border-gray-200" data-id="element-132">
              <PhoneFrame showTimer currentSlide={4} data-id="element-133">
                <SlideFour onSelectCTA={noop} onBack={noop} selectedOption={0} elapsedSeconds={14} data-id="element-134" />
              </PhoneFrame>
              <FeedbackArea title="Screen 5: CTA Options" description="The timer stops and shows the 'Promise kept' message. Presents two clear conversion paths." data-id="element-135" />
            </div>

            {/* Screen 5: Form */}
            <div className="review-row flex gap-10" data-id="element-136">
              <PhoneFrame currentSlide={5} data-id="element-137">
                <SlideFive ctaOption="web-meeting" onBack={noop} data-id="element-138" />
              </PhoneFrame>
              <FeedbackArea title="Screen 6: Intake Form" description="The final conversion step collecting user details with GDPR consent." data-id="element-139" />
            </div>
          </div>
        </I18nProvider>
      </div>
    </div>;
}